import { Decimal } from 'decimal.js';
import { z } from 'zod';
import { inclusiveLineTax } from '../invoicing/inclusive.line.tax';
import { QuotePro } from '../quotes/quote.pro';
import { PrepaymentSetting } from '../settings/general.settings';
import { TaxRate } from '../taxrate/tax.rate';
import { PolicyOptionVisibility } from './policy';
import { QuotePolicy, QuotePolicyJobConvertSplitType, QuotePolicyPrepaymentRule, QuotePolicyPrepaymentRuleOperator, QuotePolicyPrepaymentRuleType } from './quote.policy.types';

export const defaultQuotePolicy: QuotePolicy = {
    _id: null,
    isDefault: false,
    active: true,
    name: '',
    __v: 0,
    updatedBy: null,
    createdBy: null,
    // pricing: {
    //     _id: null,
    //     costSource: CostSource.StandardCost,
    //     pricingMethod: PricingMethod.Margin,
    //     multiplier: 30,
    //     priceList: null
    // },
    acceptance: {
        _id: null,
        poNumber: PolicyOptionVisibility.Disabled,
        attachments: PolicyOptionVisibility.Disabled,
        signature: PolicyOptionVisibility.Disabled,
        allowOnlineAcceptance: true,
    },
    jobConversion: {
        _id: null,
        autoConvert: false,
        copyActivities: false,
        split: QuotePolicyJobConvertSplitType.ItemCategory,
        jobTemplate: null
    },
    prepayments: {
        _id: null,
        enabled: false,
        allowOverride: false,
        rules: [],
        settings: null
    },
    reminders: {
        _id: null,
        enableForAcceptance: false,
        acceptance: [],
        enableForPayment: false,
        payment: []
    },
    notifications: {
        _id: null,
        enabled: false,
        accept: null,
        decline: null,
        prepaid: null
    },
    defaults: {
        _id: null,
        adjustmentType: 'Percent',
        expiryDays: 30
    },
}

function canProcessPrepaymentRule(rule: QuotePolicyPrepaymentRule, quote: QuotePro) {

    const schema = z.object({
        amount: z.number().min(0),
        quoteValue: z.number().min(0),
        totalSellEx: z.number().min(0),
    });

    return schema.safeParse({
        amount: rule.amount,
        quoteValue: rule.quoteValue,
        totalSellEx: quote.totalSellEx
    });
}

function calculateRulePrepayment(rule: QuotePolicyPrepaymentRule, quote: QuotePro) {
    if (!canProcessPrepaymentRule(rule, quote)) return 0;

    switch (rule.prepaymentType) {
        case QuotePolicyPrepaymentRuleType.Amount:
            return rule.amount;
        case QuotePolicyPrepaymentRuleType.Percentage:
            return (quote.totalSellInc / 100) * rule.amount;
        default:
            return 0;
    }
}

const quotePolicyPrepaymentHelper = {
    // [QuotePolicyPrepaymentRuleOperator.Equal]: (rule: QuotePolicyPrepaymentRule, quote: QuotePro) => {
    //     return quote.totalSellEx === rule.quoteValue
    //         ? calculateRulePrepayment(rule, quote)
    //         : 0;
    // },

    [QuotePolicyPrepaymentRuleOperator.GreaterThan]: (rule: QuotePolicyPrepaymentRule, quote: QuotePro) => {
        return quote.totalSellInc > rule.quoteValue
            ? calculateRulePrepayment(rule, quote)
            : 0;
    },

    [QuotePolicyPrepaymentRuleOperator.GreaterThanOrEqual]: (rule: QuotePolicyPrepaymentRule, quote: QuotePro) => {
        return quote.totalSellInc >= rule.quoteValue
            ? calculateRulePrepayment(rule, quote)
            : 0;
    },

    [QuotePolicyPrepaymentRuleOperator.LessThan]: (rule: QuotePolicyPrepaymentRule, quote: QuotePro) => {
        return quote.totalSellInc < rule.quoteValue
            ? calculateRulePrepayment(rule, quote)
            : 0;
    },

    [QuotePolicyPrepaymentRuleOperator.LessThanOrEqual]: (rule: QuotePolicyPrepaymentRule, quote: QuotePro) => {
        return quote.totalSellInc <= rule.quoteValue
            ? calculateRulePrepayment(rule, quote)
            : 0;
    }


}

export const calculateQuotePrepayment = (quote: QuotePro) => {

    if (!quote?.policy?.prepayments?.enabled) return 0;

    for (const rule of quote.policy.prepayments.rules ?? []) {
        const value = quotePolicyPrepaymentHelper[rule.operator]?.(rule, quote) ?? 0;
        if (value > 0) return value;
    }

    return 0;
}

/**
 * The tax rate the quote's prepayment invoice line will carry, so every figure that promises that
 * invoice's GST reads the same rate it bills.
 *
 * The policy snapshot's settings win as a whole, so a snapshot without a rate means no tax even when
 * the tenant has one; only an absent snapshot falls to the tenant setting. No rate is no GST: the
 * invoice never infers one from the quote's lines.
 */
export function prepaymentInvoiceTaxRate(policy: QuotePolicy | null | undefined, tenantPrepayment: PrepaymentSetting | null | undefined): TaxRate | null {

    return (policy?.prepayments?.settings ?? tenantPrepayment)?.saleTaxRate ?? null;
}

/**
 * Rounds a quote's prepayment figures to the exact cents the prepayment invoice will bill.
 *
 * The invoice line is tax-inclusive: its amount rounds to 4dp then 2dp and its tax comes from
 * `inclusiveLineTax` on that rounded amount. Pass the rate from `prepaymentInvoiceTaxRate`.
 */
export const roundQuotePrepayment = (prepaymentInc: number, taxRate: TaxRate | null | undefined) => {

    const rate = (taxRate?.rate ?? 0) / 100;
    const safeInc = Number.isFinite(Number(prepaymentInc)) ? Number(prepaymentInc) : 0;
    const inc = new Decimal(safeInc).toDP(4).toDP(2);
    const tax = inclusiveLineTax(inc.toNumber(), rate);
    const ex = inc.minus(tax);

    return { prepaymentInc: inc.toNumber(), prepaymentTax: tax, prepaymentEx: ex.toNumber() };
}
